import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * Usually a plain string. May also be a fragment mixing plain text with an
   * inline interactive element (e.g. a "Terms and Conditions" link inside a
   * consent label, MYN-33) - the nested control must call
   * `e.stopPropagation()` on click so it doesn't also toggle the checkbox via
   * the native label-forwards-click-to-control behaviour.
   */
  label: ReactNode;
  /**
   * `default` = native checkbox before the label (existing look).
   * `toggle` = label on the left, a green rounded check-box toggle on the
   * right (Figma Founder Preferences rows, US-101/US-040).
   */
  variant?: 'default' | 'toggle';
  /**
   * Keeps `label` as the accessible name but visually hides the text (`sr-only`)
   * instead of rendering it next to the box. For bare selection checkboxes
   * (e.g. a `DataTable` row/header select-all checkbox) where a visible label
   * would duplicate what the row already shows. Only supported on the
   * `default` variant - `toggle` always shows its label by design.
   */
  hideLabel?: boolean;
}

function CheckGlyph() {
  // Always rendered white; invisible against the box's own white idle
  // background and only shows contrast once the box turns green - avoids
  // needing a nested peer-selector (Tailwind's peer-checked can't reach past
  // one sibling level to a descendant of the box).
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2.5 6.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Design-system checkbox primitive (styled native checkbox with an associated
 * visible label). Used for boolean flags and multi-select option groups.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, variant = 'default', hideLabel = false, className = '', ...props },
  ref,
) {
  const id = useId();

  if (variant === 'toggle') {
    return (
      <label htmlFor={id} className="flex w-full cursor-pointer items-center justify-between gap-3 text-sm text-navy">
        <span>{label}</span>
        <input id={id} ref={ref} type="checkbox" className={`peer sr-only ${className}`.trim()} {...props} />
        <span className="flex size-5 shrink-0 items-center justify-center rounded-md border border-input-border bg-white transition-colors peer-checked:border-green peer-checked:bg-green peer-focus-visible:ring-2 peer-focus-visible:ring-dkgreen">
          <CheckGlyph />
        </span>
      </label>
    );
  }

  return (
    // `relative` is load-bearing, not cosmetic: the visually-hidden label span
    // below uses Tailwind's `sr-only`, which is `position: absolute`. Without a
    // positioned ancestor its containing block is the initial containing block,
    // so `overflow: hidden`/`auto` on a statically-positioned ancestor cannot
    // clip it - 20 of these (one per table row) stretched the document to 1522px
    // and made the whole admin page scroll behind a supposedly clipped shell.
    <label htmlFor={id} className="relative flex items-center gap-2 text-sm text-text-primary">
      <input
        id={id}
        ref={ref}
        type="checkbox"
        className={`h-4 w-4 rounded border-input-border text-dkgreen focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dkgreen ${className}`.trim()}
        {...props}
      />
      <span className={hideLabel ? 'sr-only' : undefined}>{label}</span>
    </label>
  );
});

Checkbox.displayName = 'Checkbox';
